/**
 * HAD Clothing - Wholesale Features JavaScript
 * Lead time calculator, quote form validation, and B2B tools
 */

// Lead Time Calculator
class LeadTimeCalculator {
  constructor() {
    this.typeSelect = document.getElementById('calc-type');
    this.qtySelect = document.getElementById('calc-qty');
    this.shipSelect = document.getElementById('calc-ship');
    this.resultElement = document.getElementById('calc-result');

    if (this.typeSelect && this.qtySelect && this.shipSelect && this.resultElement) {
      this.init();
    }
  }

  init() {
    [this.typeSelect, this.qtySelect, this.shipSelect].forEach(select => {
      select.addEventListener('change', () => this.calculate());
    });

    // Calculate on page load
    this.calculate();
  }

  calculate() {
    const type = parseInt(this.typeSelect.value);
    const qty = parseInt(this.qtySelect.value);
    const ship = parseInt(this.shipSelect.value);

    const minWeeks = type + qty + ship;
    const maxWeeks = minWeeks + 2;

    this.resultElement.textContent = `${minWeeks}-${maxWeeks} weeks`;

    // Add animation
    this.resultElement.style.animation = 'none';
    setTimeout(() => {
      this.resultElement.style.animation = 'fadeIn 0.3s ease';
    }, 10);
  }
}

// Quote Form Validation
class WholesaleQuoteForm {
  constructor() {
    this.form = document.querySelector('.wholesale-quote__form');

    if (this.form) {
      this.init();
    }
  }

  init() {
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    this.attachInputValidation();
    this.initQuantityCalculator();
  }

  handleSubmit(e) {
    e.preventDefault();

    if (this.validateForm()) {
      this.submitForm();
    }
  }

  validateForm() {
    let isValid = true;
    const requiredFields = this.form.querySelectorAll('[required]');

    requiredFields.forEach(field => {
      this.clearError(field);

      if (!field.value.trim()) {
        this.showError(field, 'This field is required');
        isValid = false;
      } else if (field.type === 'email' && !this.isValidEmail(field.value)) {
        this.showError(field, 'Please enter a valid email address');
        isValid = false;
      } else if (field.type === 'tel' && !this.isValidPhone(field.value)) {
        this.showError(field, 'Please enter a valid phone number');
        isValid = false;
      }
    });

    return isValid;
  }

  showError(field, message) {
    field.classList.add('error');

    const errorElement = document.createElement('span');
    errorElement.className = 'wholesale-quote__error';
    errorElement.textContent = message;
    errorElement.style.cssText = `
      display: block;
      color: #d32f2f;
      font-size: 11px;
      margin-top: 5px;
    `;

    field.parentElement.appendChild(errorElement);
  }

  clearError(field) {
    field.classList.remove('error');
    const error = field.parentElement.querySelector('.wholesale-quote__error');
    if (error) {
      error.remove();
    }
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  isValidPhone(phone) {
    return /^[\d\s\-\+\(\)]+$/.test(phone);
  }

  attachInputValidation() {
    const inputs = this.form.querySelectorAll('input, textarea, select');

    inputs.forEach(input => {
      input.addEventListener('blur', () => {
        if (input.hasAttribute('required') && !input.value.trim()) {
          this.showError(input, 'This field is required');
        }
      });

      input.addEventListener('input', () => {
        if (input.classList.contains('error')) {
          this.clearError(input);
        }
      });
    });
  }

  initQuantityCalculator() {
    const qtyInput = this.form.querySelector('[name="quantity"]');
    const priceDisplay = this.form.querySelector('.wholesale-quote__estimated-price');

    if (qtyInput && priceDisplay) {
      qtyInput.addEventListener('input', () => {
        const qty = parseInt(qtyInput.value) || 0;
        const estimatedPrice = this.calculateEstimatedPrice(qty);
        priceDisplay.textContent = `Estimated Price: ${estimatedPrice}`;
      });
    }
  }

  calculateEstimatedPrice(quantity) {
    // Example pricing tiers
    let pricePerUnit;

    if (quantity >= 1000) {
      pricePerUnit = 8.50;
    } else if (quantity >= 500) {
      pricePerUnit = 10.00;
    } else if (quantity >= 100) {
      pricePerUnit = 12.00;
    } else {
      pricePerUnit = 15.00;
    }

    const total = quantity * pricePerUnit;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(total);
  }

  submitForm() {
    const submitButton = this.form.querySelector('.wholesale-quote__submit');
    const originalText = submitButton.textContent;

    // Show loading state
    submitButton.textContent = 'SUBMITTING...';
    submitButton.disabled = true;

    // Collect form data
    const formData = new FormData(this.form);

    // Submit via AJAX (adjust endpoint as needed)
    fetch('/contact', {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      this.showSuccess();
      this.form.reset();
    })
    .catch(error => {
      this.showSubmitError();
    })
    .finally(() => {
      submitButton.textContent = originalText;
      submitButton.disabled = false;
    });
  }

  showSuccess() {
    const successMessage = document.createElement('div');
    successMessage.className = 'wholesale-quote__success';
    successMessage.innerHTML = `
      <div style="
        background: #4caf50;
        color: white;
        padding: 20px;
        text-align: center;
        margin-bottom: 20px;
        animation: fadeInDown 0.3s ease;
      ">
        ✓ Thank you! Your quote request has been submitted. We'll contact you within 24 hours.
      </div>
    `;

    this.form.insertAdjacentElement('beforebegin', successMessage);

    setTimeout(() => {
      successMessage.remove();
    }, 5000);
  }

  showSubmitError() {
    const errorMessage = document.createElement('div');
    errorMessage.className = 'wholesale-quote__submit-error';
    errorMessage.innerHTML = `
      <div style="
        background: #f44336;
        color: white;
        padding: 20px;
        text-align: center;
        margin-bottom: 20px;
      ">
        ✗ There was an error submitting your request. Please try again or contact us directly.
      </div>
    `;

    this.form.insertAdjacentElement('beforebegin', errorMessage);

    setTimeout(() => {
      errorMessage.remove();
    }, 5000);
  }
}

// Sample Order Modal
class SampleOrderModal {
  constructor() {
    this.triggers = document.querySelectorAll('[data-sample-modal]');

    if (this.triggers.length > 0) {
      this.init();
    }
  }

  init() {
    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.openModal();
      });
    });
  }

  openModal() {
    // Create modal if it doesn't exist
    if (!document.getElementById('sample-modal')) {
      this.createModal();
    }

    const modal = document.getElementById('sample-modal');
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  }

  createModal() {
    const modal = document.createElement('div');
    modal.id = 'sample-modal';
    modal.style.cssText = `
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      z-index: 9999;
      align-items: center;
      justify-content: center;
      padding: 20px;
    `;

    modal.innerHTML = `
      <div style="
        background: white;
        max-width: 500px;
        width: 100%;
        padding: 40px;
        position: relative;
      ">
        <button onclick="this.closest('#sample-modal').style.display='none'; document.body.style.overflow=''" style="
          position: absolute;
          top: 15px;
          right: 15px;
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #999;
        ">&times;</button>

        <h3 style="
          font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
          font-size: 20px;
          font-weight: 600;
          letter-spacing: 2px;
          text-transform: uppercase;
          margin-bottom: 20px;
        ">ORDER SAMPLES</h3>

        <p style="color: #666; margin-bottom: 20px;">
          Select the styles you'd like to sample. Sample cost is credited toward your first bulk order.
        </p>

        <form>
          <div style="margin-bottom: 15px;">
            <label style="display: block; margin-bottom: 5px; font-size: 13px; font-weight: 600;">Email Address</label>
            <input type="email" required style="width: 100%; padding: 12px; border: 1px solid #ddd;">
          </div>

          <div style="margin-bottom: 15px;">
            <label style="display: block; margin-bottom: 5px; font-size: 13px; font-weight: 600;">Select Styles</label>
            <textarea placeholder="List style codes or describe what you'd like to sample" style="width: 100%; padding: 12px; border: 1px solid #ddd; min-height: 100px;"></textarea>
          </div>

          <button type="submit" style="
            width: 100%;
            background: #1a1a1a;
            color: white;
            border: none;
            padding: 15px;
            font-weight: 600;
            letter-spacing: 2px;
            cursor: pointer;
          ">SUBMIT REQUEST</button>
        </form>
      </div>
    `;

    document.body.appendChild(modal);
  }
}

// Pricing Calculator
class PricingCalculator {
  constructor() {
    this.calculatorElements = document.querySelectorAll('[data-pricing-calculator]');

    if (this.calculatorElements.length > 0) {
      this.init();
    }
  }

  init() {
    this.calculatorElements.forEach(calc => {
      const qtyInput = calc.querySelector('[data-qty-input]');
      const resultDisplay = calc.querySelector('[data-price-result]');

      if (qtyInput && resultDisplay) {
        qtyInput.addEventListener('input', () => {
          const price = this.calculatePrice(parseInt(qtyInput.value) || 0);
          resultDisplay.textContent = price;
        });
      }
    });
  }

  calculatePrice(quantity) {
    const basePrices = {
      50: 15.00,
      250: 12.00,
      500: 10.00,
      1000: 8.50
    };

    let pricePerUnit = basePrices[50];

    for (const [minQty, price] of Object.entries(basePrices)) {
      if (quantity >= parseInt(minQty)) {
        pricePerUnit = price;
      }
    }

    const total = quantity * pricePerUnit;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(total);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWholesale);
} else {
  initWholesale();
}

function initWholesale() {
  new LeadTimeCalculator();
  new WholesaleQuoteForm();
  new SampleOrderModal();
  new PricingCalculator();
}

// Export for use in other scripts
window.HADWholesale = {
  LeadTimeCalculator,
  WholesaleQuoteForm,
  SampleOrderModal,
  PricingCalculator
};

// Global function for inline calculator (for backward compatibility)
window.calculateLeadTime = function() {
  const calc = new LeadTimeCalculator();
  calc.calculate();
};
